import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createCommunity,
  getCommunity,
  joinCommunity,
  leaveCommunity,
  listCommunities,
  listMyCommunities,
  updateCommunity,
} from '@/api/communities'
import type { CommunityDetail, CreateCommunityRequest, UpdateCommunityRequest } from '@/api/types'

export const COMMUNITIES_QUERY_KEY = ['communities'] as const

export const useCommunities = (page: number = 0) =>
  useQuery({
    queryKey: [...COMMUNITIES_QUERY_KEY, 'list', page],
    queryFn: () => listCommunities(page, 20),
  })

export const useCommunity = (slug: string | undefined) =>
  useQuery({
    queryKey: [...COMMUNITIES_QUERY_KEY, 'detail', slug],
    queryFn: () => getCommunity(slug as string),
    enabled: Boolean(slug),
    retry: (failureCount, error) => {
      const status = (error as { status?: number })?.status
      if (status === 404) return false
      return failureCount < 3
    },
  })

export const useMyCommunities = () =>
  useQuery({
    queryKey: [...COMMUNITIES_QUERY_KEY, 'me'],
    queryFn: () => listMyCommunities(),
  })

export const useCreateCommunity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateCommunityRequest) => createCommunity(data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: COMMUNITIES_QUERY_KEY })
      queryClient.setQueryData<CommunityDetail>(
        [...COMMUNITIES_QUERY_KEY, 'detail', created.slug],
        created
      )
    },
  })
}

export const useUpdateCommunity = (slug: string) => {
  const queryClient = useQueryClient()
  const detailKey = [...COMMUNITIES_QUERY_KEY, 'detail', slug]
  return useMutation<unknown, unknown, UpdateCommunityRequest, { previous?: CommunityDetail }>({
    mutationFn: (data: UpdateCommunityRequest) => updateCommunity(slug, data),
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<CommunityDetail>(detailKey)
      if (previous) {
        queryClient.setQueryData<CommunityDetail>(detailKey, {
          ...previous,
          name: data.name ?? previous.name,
          description: data.description ?? previous.description,
          accentColor: data.accentColor ?? previous.accentColor,
          heroImageKey: data.heroImageKey ?? previous.heroImageKey,
        })
      }
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(detailKey, ctx.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
      queryClient.invalidateQueries({ queryKey: [...COMMUNITIES_QUERY_KEY, 'list'] })
    },
  })
}

export const useJoinCommunity = (slug: string) => {
  const queryClient = useQueryClient()
  const detailKey = [...COMMUNITIES_QUERY_KEY, 'detail', slug]
  return useMutation<unknown, unknown, void, { previous?: CommunityDetail }>({
    mutationFn: () => joinCommunity(slug),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<CommunityDetail>(detailKey)
      if (previous) {
        queryClient.setQueryData<CommunityDetail>(detailKey, {
          ...previous,
          memberCount: previous.memberCount + 1,
          viewerMembership: 'MEMBER',
        })
      }
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(detailKey, ctx.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
      queryClient.invalidateQueries({ queryKey: [...COMMUNITIES_QUERY_KEY, 'me'] })
    },
  })
}

export const useLeaveCommunity = (slug: string) => {
  const queryClient = useQueryClient()
  const detailKey = [...COMMUNITIES_QUERY_KEY, 'detail', slug]
  return useMutation<unknown, unknown, void, { previous?: CommunityDetail }>({
    mutationFn: () => leaveCommunity(slug),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<CommunityDetail>(detailKey)
      if (previous) {
        queryClient.setQueryData<CommunityDetail>(detailKey, {
          ...previous,
          memberCount: Math.max(0, previous.memberCount - 1),
          viewerMembership: null,
        })
      }
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(detailKey, ctx.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: detailKey })
      queryClient.invalidateQueries({ queryKey: [...COMMUNITIES_QUERY_KEY, 'me'] })
    },
  })
}
